/**
 * Today's Office strip — anonymous, ephemeral aggregate shown on the win screen.
 *
 * Voice + visual rules per PRD-LEADERBOARD §10 + brand DNA tokens only:
 *  - Eyebrow: Fraunces 13px caps, neutral.700.
 *  - Header title: Fraunces 16px 600, ink-black.
 *  - Stat label: JetBrains Mono 11px caps, neutral.500.
 *  - Stat value: JetBrains Mono 24px tabular, ink-black.
 *  - "You" stat value background: acid-yellow (--accent-300). ONLY on the
 *    player's own stat — brand DNA rule.
 *  - Container: 1px standard border, 4px radius, 24px padding, cream-card
 *    surface, no shadow.
 *
 * States:
 *  - 'loading'  — initial render, "—" placeholders, 0.4 opacity.
 *  - 'empty'    — submit accepted but samples < 10. Shows fastest if any.
 *  - 'ready'    — full data: median + fastest + you + sentence.
 *  - 'hidden'   — server kill, error, timeout, disabled. display: none.
 */

import type { ScoreSubmitResponse } from '@/types/leaderboard';
import { countryToFlag } from '@/ui/countries';

export type StripState = 'loading' | 'empty' | 'ready' | 'hidden';

export interface MountStripOpts {
  numericId: string;
  /** Player's own seconds — used for the "You" stat and the rank sentence. */
  playerSeconds: number;
  /** Default 19 — Gary's pace declared in the eyebrow. */
  pacePar?: number;
}

export interface StripHandle {
  element: HTMLElement;
  setState(state: StripState, data?: ScoreSubmitResponse | null): void;
}

/**
 * Build the strip DOM. Initial state: 'loading'. Caller drives state changes
 * via setState() once the submit response (or failure) is known.
 */
export function createTodayStrip(opts: MountStripOpts): StripHandle {
  const pacePar = opts.pacePar ?? 19;
  const aside = document.createElement('aside');
  aside.className = 'today-strip';
  aside.setAttribute('data-state', 'loading');
  aside.setAttribute('aria-label', "Today's office stats");

  aside.innerHTML = `
    <p class="today-strip__eyebrow" data-field="eyebrow">World record · ${pacePar}s · Gary 🇦🇺</p>
    <div class="today-strip__header">
      <span class="today-strip__title">All-time leaderboard</span>
      <span class="today-strip__id">${escapeHtml(opts.numericId.startsWith('CB-') ? opts.numericId : `CB-${opts.numericId}`)}</span>
    </div>
    <dl class="today-strip__stats">
      <div class="today-strip__stat">
        <dt>Median</dt>
        <dd data-field="median">—</dd>
      </div>
      <div class="today-strip__stat">
        <dt>Fastest</dt>
        <dd data-field="fastest">—</dd>
      </div>
      <div class="today-strip__stat today-strip__stat--you">
        <dt>You</dt>
        <dd data-field="you">${formatSeconds(opts.playerSeconds)}</dd>
      </div>
    </dl>
    <p class="today-strip__sentence" data-field="sentence" aria-live="polite"></p>
    <ol class="today-strip__board" data-field="board" aria-label="All-time leaderboard"></ol>
    <p class="today-strip__yourrank" data-field="yourrank" aria-live="polite"></p>
    <p class="today-strip__footnote">
      Country and nickname optional · <a href="/privacy/">how this works</a>
    </p>
  `;

  function setState(state: StripState, data?: ScoreSubmitResponse | null): void {
    aside.setAttribute('data-state', state);

    const medianEl = aside.querySelector('[data-field="median"]') as HTMLElement | null;
    const fastestEl = aside.querySelector('[data-field="fastest"]') as HTMLElement | null;
    const youEl = aside.querySelector('[data-field="you"]') as HTMLElement | null;
    const sentenceEl = aside.querySelector('[data-field="sentence"]') as HTMLElement | null;

    if (state === 'hidden') {
      // The CSS hides via [data-state="hidden"] { display: none }. Nothing else.
      return;
    }

    if (state === 'loading') {
      if (medianEl) medianEl.textContent = '—';
      if (fastestEl) fastestEl.textContent = '—';
      if (youEl) youEl.textContent = formatSeconds(opts.playerSeconds);
      if (sentenceEl) sentenceEl.textContent = '';
      return;
    }

    if (!data || !data.accepted) {
      // Should be set to 'hidden' by caller; guard.
      aside.setAttribute('data-state', 'hidden');
      return;
    }

    if (youEl) youEl.textContent = formatSeconds(opts.playerSeconds);

    // Use the visible leaderboard's #1 as the "Fastest" stat AND the world-record
    // eyebrow — guaranteed correlation between header copy and the board below.
    const topEntries = Array.isArray(data.top_entries) ? data.top_entries : [];
    const top1 = topEntries[0];
    const aggregateFastestSafe = data.fastest != null ? data.fastest : Infinity;
    const recordSeconds = top1
      ? Math.min(top1.seconds, aggregateFastestSafe)
      : aggregateFastestSafe === Infinity
        ? null
        : aggregateFastestSafe;
    const recordHolder = top1 ?? null;

    if (fastestEl) fastestEl.textContent = recordSeconds != null ? formatSeconds(recordSeconds) : '—';
    if (medianEl) medianEl.textContent = data.median != null ? formatSeconds(data.median) : '—';

    const eyebrowEl = aside.querySelector('[data-field="eyebrow"]') as HTMLElement | null;
    if (eyebrowEl && recordHolder && recordSeconds != null) {
      const flag = countryToFlag(recordHolder.country);
      eyebrowEl.textContent = `World record · ${formatSeconds(recordSeconds)} · ${recordHolder.nickname} ${flag}`;
    }

    // Always render the top-N board so Gary (the world-record holder) is
    // visible from launch onward, even before 10 samples are accumulated.
    const boardEl = aside.querySelector('[data-field="board"]') as HTMLElement | null;
    const yourRankEl = aside.querySelector('[data-field="yourrank"]') as HTMLElement | null;
    if (boardEl) {
      boardEl.innerHTML = '';
      const entries = Array.isArray(data.top_entries) ? data.top_entries : [];
      for (const e of entries) {
        const li = document.createElement('li');
        li.className = 'today-strip__row' + (e.is_you ? ' today-strip__row--you' : '');
        li.innerHTML = `
          <span class="today-strip__rank">${e.rank}</span>
          <span class="today-strip__flag" aria-label="${escapeHtml(e.country)}">${countryToFlag(e.country)}</span>
          <span class="today-strip__nick">${escapeHtml(e.nickname)}</span>
          <span class="today-strip__time">${formatSeconds(e.seconds)}</span>
        `;
        boardEl.appendChild(li);
      }
    }
    if (yourRankEl) {
      if (data.your_rank != null && data.your_rank > 10) {
        yourRankEl.textContent = `You: rank ${data.your_rank}`;
      } else {
        yourRankEl.textContent = '';
      }
    }

    if (data.samples < 10 || data.percentile === null) {
      // Empty state: room is still warming up — but Gary's record + your time
      // are still on display above.
      if (sentenceEl) sentenceEl.textContent = 'The room is warming up. Beat Gary if you can.';
      aside.setAttribute('data-state', 'empty');
      return;
    }

    if (sentenceEl) sentenceEl.textContent = sentenceForPercentile(data.percentile);
    aside.setAttribute('data-state', 'ready');
  }

  return { element: aside, setState };
}

/**
 * Voice copy templates per PRD-LEADERBOARD §10.
 *
 *   ≥ 90  → "Top 10% of the room."
 *   ≥ 50  → "Quicker than {percentile}% of the room."
 *   ≥ 10  → "{100-percentile}% of the room are slower."
 *   < 10  → "Bottom of the kitchen this morning."
 *
 * "Bottom of the kitchen" is dry-AU brand voice — lifted directly from the
 * spec. Steve voice-gates the final string before launch.
 */
export function sentenceForPercentile(percentile: number): string {
  if (percentile >= 90) return 'Top 10% of the room.';
  if (percentile >= 50) return `Quicker than ${percentile}% of the room.`;
  if (percentile >= 10) return `${100 - percentile}% of the room are slower.`;
  return 'Bottom of the kitchen this morning.';
}

function formatSeconds(s: number): string {
  return `${Math.round(s)}s`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
